import * as Y from 'yjs';
import {
  Awareness,
  encodeAwarenessUpdate,
  applyAwarenessUpdate,
  removeAwarenessStates,
} from 'y-protocols/awareness';
import { u8ToBase64, base64ToU8 } from './yjsBase64';

/**
 * Supabase Realtime(broadcast) 를 전송 계층으로 쓰는 경량 Yjs 프로바이더.
 * - 별도 서버 없이 같은 page 를 보는 클라이언트끼리 CRDT 업데이트/awareness(커서) 를 교환.
 * - 권한 있는 클라이언트만 채널에 접근(RLS 와 별개로, 채널명은 page id 기반).
 * - 초기 동기화: 구독 직후 sync-request 를 보내 기존 접속자의 전체 상태를 받아 병합.
 */
export default class SupabaseProvider {
  constructor(supabase, room, doc, awareness) {
    this.doc = doc;
    this.awareness = awareness || new Awareness(doc);
    this.synced = false;

    this.channel = supabase.channel(`yjs:${room}`, {
      config: { broadcast: { self: false, ack: false } },
    });

    // 로컬 doc 변경 → 브로드캐스트
    this._onDocUpdate = (update, origin) => {
      if (origin === this) return; // 원격에서 적용된 변경은 다시 보내지 않음
      this.channel.send({
        type: 'broadcast',
        event: 'y-update',
        payload: { u: u8ToBase64(update) },
      });
    };
    doc.on('update', this._onDocUpdate);

    // 로컬 awareness(커서/선택) 변경 → 브로드캐스트
    this._onAwareness = ({ added, updated, removed }) => {
      const changed = added.concat(updated, removed);
      const payload = u8ToBase64(encodeAwarenessUpdate(this.awareness, changed));
      this.channel.send({ type: 'broadcast', event: 'y-awareness', payload: { u: payload } });
    };
    this.awareness.on('update', this._onAwareness);

    this.channel
      .on('broadcast', { event: 'y-update' }, ({ payload }) => {
        try { Y.applyUpdate(doc, base64ToU8(payload.u), this); } catch (e) { /* noop */ }
      })
      .on('broadcast', { event: 'y-awareness' }, ({ payload }) => {
        try { applyAwarenessUpdate(this.awareness, base64ToU8(payload.u), this); } catch (e) { /* noop */ }
      })
      .on('broadcast', { event: 'y-sync-request' }, () => {
        // 신규 접속자에게 내 전체 상태 + awareness 를 보냄
        this.channel.send({
          type: 'broadcast', event: 'y-update',
          payload: { u: u8ToBase64(Y.encodeStateAsUpdate(doc)) },
        });
        const ids = Array.from(this.awareness.getStates().keys());
        if (ids.length) {
          this.channel.send({
            type: 'broadcast', event: 'y-awareness',
            payload: { u: u8ToBase64(encodeAwarenessUpdate(this.awareness, ids)) },
          });
        }
      })
      .subscribe((status) => {
        if (status === 'SUBSCRIBED') {
          this.synced = true;
          this.channel.send({ type: 'broadcast', event: 'y-sync-request', payload: {} });
          // 내 awareness 도 즉시 알림
          const ids = Array.from(this.awareness.getStates().keys());
          if (ids.length) {
            this.channel.send({
              type: 'broadcast', event: 'y-awareness',
              payload: { u: u8ToBase64(encodeAwarenessUpdate(this.awareness, ids)) },
            });
          }
        }
      });
  }

  destroy() {
    try { this.doc.off('update', this._onDocUpdate); } catch (e) { /* noop */ }
    // ⚠️ 순서 중요: removeAwarenessStates 를 _onAwareness 가 아직 붙어있는 상태에서 호출해야
    // "내 상태 제거(null)" 가 채널로 브로드캐스트되어 상대 화면의 내 커서/라벨이 즉시 사라진다.
    // (핸들러를 먼저 떼면 상대는 awareness outdated 타임아웃(30초)까지 유령 커서를 보게 됨.)
    try { removeAwarenessStates(this.awareness, [this.doc.clientID], this); } catch (e) { /* noop */ }
    try { this.awareness.off('update', this._onAwareness); } catch (e) { /* noop */ }
    try { this.channel.unsubscribe(); } catch (e) { /* noop */ }
  }
}
